import { Color } from "cesium";

export const MASTPOINT_CONTEXT_KEY = Symbol('mastpointContext');

export const ACTIVE_PLAN_COLOR = Color.fromCssColorString('#594EF2');
export const INACTIVE_PLAN_COLOR = Color.fromCssColorString('#969696');

export const SMARTGIS_EMAIL = {
	SUPPORT_EMAIL: 'support@smartgis.no'
}

export const ENTITY_ID_PREFIX = {
    MASTPOINT: 'MastPoint:',
    POLE: 'pole:',
    LINE: 'line:',
    CROSSBEAM: 'crossbeam:',
    UTILITYLINK: 'utilityLink:',
    SUPPORT: 'support:',
    VIRTUAL_SUPPORT: 'supportPoleId:',
    /** Billboard for a Feltkatalog image (image-area tool). */
    SMARTDATA_IMAGE: 'smartdataImage:',
    /** The image-area tool's drawn polygon. */
    IMAGE_AREA: 'imageArea:'
} as const;

export const TOOLBAR_GROUP_OPTIONS = {
	NONE: 'NONE',
	ENTITY_MASTPOINT: 'ENTITY_MASTPOINT',
	ENTITY_LINES: 'ENTITY_LINES',
	ENTITY_TRENCH: 'ENTITY_TRENCH'
} as const;

export const AllowedHighVoltageTypes = [
	'',
	'Endemast',
	'Bæremast',
	'Vinkelmast',
	'Avgreningsmast'
] as const;

export const AllowedTransformers = ['', 'I stolpe', 'Mastefottrafo'] as const;

export const FoundationOptions = [
	{ value: false, label: 'Jord' },
	{ value: true, label: 'Fjell' }
];

/**
 * Plan types which are selectable when creating a plan. 
 */
export const planTypes = [
	{ label: 'Plan', value: 'Plan' },
	{ label: 'Fellesføring', value: 'Fellesføring' },
	{ label: 'Databerikelse', value: 'Databerikelse' }
] as const;

export const projectAndPlanStatus = [
	{ label: 'Utkast', value: 0 },
	{ label: 'Under planlegging', value: 1 },
	{ label: 'Ferdig planlagt', value: 2 },
	{ label: 'Ferdig utført', value: 3 },
] as const

/**
 * Koordinatregistering utført på topp eller bunn av et objekt 
 * https://register.geonorge.no/sosi-kodelister/fkb/generell/5.0/hoydereferanse
 */
export const registeredHeightReference = [
	{ label: 'Fot', value: 'Fot' },
	{ label: 'Topp', value: 'Topp' },
	{ label: 'Ukjent', value: 'Ukjent' }
] as const;

/**
 * Metode for datafangst. Datafangstmetoden beskriver hvordan selve vektordataene er posisjonert fra et datagrunnlag (observasjoner med landmålingsutstyr, fotogrammetrisk stereomodell, digital terrengmodell etc.) 
 * og ikke prosessen med å innhente det bakenforliggende datagrunnlaget. 
 * https://register.geonorge.no/sosi-kodelister/fkb/generell/5.0/datafangstmetode
 */
export const dataCaptureMethods = [
	{ label: 'Digitalisert', value: 'Dig' },
	{ label: 'Fotogrammetri', value: 'Fot' },
	{ label: 'Generert', value: 'Gen' },
	{ label: 'Landmålt', value: 'Lan' },
	{ label: 'Plandata', value: 'Pla' },
	{ label: 'Satellittmålt', value: 'Sat' },
	{ label: 'Som bygget', value: 'Byg' },
	{ label: 'Ukjent', value: 'Ukj' }
] as const;

/**
 * Metadata som beskriver hvor godt objektene framgår i datagrunnlaget for posisjonering (f.eks. flybildene). 
 * https://register.geonorge.no/sosi-kodelister/fkb/generell/5.0/synbarhet
 */ 
export const visibilityEnum = [
    { label: 'Ikke registrert', value: ""},
	{ label: 'Fullt ut synlig', value: "FullVisibility" },
	{ label: 'Dårlig gjenfinnbar i terreng', value: "PoorVisibility" },
	{ label: 'Middels synlig', value: "PartialVisibility" },
	{ label: 'Ikke synlig', value: "NoVisibility" }
] as const;


export const luftfartshindermerking = [
	{ label: '', value: null },
	{ label: 'Fargemerking', value: 'ColorMarking' },
	{ label: 'Markør', value: 'Marker' },
] as const;

export const luftfartshinderlyssetting = [
	{ label: '', value: null },
	{ label: 'Fast rød', value: 'FixedRed' },
	{ label: 'Fast hvit', value: 'FixedWhite' },
	{ label: 'Blinkende rød', value: 'FlashingRed' },
	{ label: 'Blinkende hvit', value: 'FlashingWhite' },
	{ label: 'Høyintensitetstype A', value: 'HighIntensityTypeA' },
	{ label: 'Høyintensitetstype B', value: 'HighIntensityTypeB' },
	{ label: 'Lyssatt', value: 'Lighted' },
	{ label: 'Belyst med flomlys', value: 'LitByFloodLighting' },
	{ label: 'Lavintensitetstype A', value: 'LowIntensityTypeA' },
	{ label: 'Lavintensitetstype B', value: 'LowIntensityTypeB' },
	{ label: 'Mellom intensitet type A', value: 'MediumIntensityTypeA' },
	{ label: 'Mellom intensitet type B', value: 'MediumIntensityTypeB' },
	{ label: 'Mellom intensitet type C', value: 'MediumIntensityTypeC' },
] as const;

// Lines can be connected in different ways. This enum makes sure we have a standardized way of referring to them.
// 1. Line is connected to the mastpoint but no component. It is "FLOATING"
// 2. Line is connected to a component on the mastpoint. It is "FIXED"
// 3. Line is not connected, but have a height above terrain. E.g. a house or ground point. It is "UNKNOWN"
export const LINE_CONNECTION_TYPES = {
	FLOATING: 'FLOATING',
	FIXED: 'FIXED',
	UNKNOWN: 'UNKNOWN'
} as const;


// Maps to https://github.com/smartgis-as/SmartGIS-API/blob/2455240e61aab1cd89301ec4981082fbc7c1c775/src/SmartGIS.Application/Models/Statics/ConditionTypes.cs
export const ConditionTypeIds = {
	OutsideDamage: 6,
	WoodpeckerDamage: 7,
	InsideDamage: 8,
	RotPocketDamage: 9,

	//CrackDamage: 10,
	//AntDamage: 11,

	ConditionUniversal: 72,
	OutsideRot: 83
};

export const poleTypeOptions: { value: string; title: string }[] = [
	{
		value: 'A',
		title: 'Avgreningsmast'
	},
	{
		value: 'B',
		title: 'Bæremast'
	},
	{
		value: 'BA',
		title: 'BæreAvspenningsmast'
	},
	{
		value: 'E',
		title: 'Endemast'
	},
	{
		value: 'V',
		title: 'Vinkelmast'
	}
];

export const utmZoneOptions: number[] = [32, 33, 34, 35, 36];

export const globalDefaultUtmZone = 32;

export const globalNewLineHeight = 4;

export const legalVoltageOptions: { label: string; value: number }[] = [
	{
		label: '0',
		value: 0
	},
	{
		label: '0.23',
		value: 0.23
	},
	{
		label: '0.40',
		value: 0.4
	},
	{
		label: '1',
		value: 1
	},
	{
		label: '3',
		value: 3
	},
	{
		label: '6',
		value: 6
	},
	{
		label: '10',
		value: 10
	},
	{
		label: '15',
		value: 15
	},
	{
		label: '20',
		value: 20
	},
	{
		label: '30',
		value: 30
	},
	{
		label: '45',
		value: 45
	},
	{
		label: '66',
		value: 66
	},
	{
		label: '90',
		value: 90
	},
	{
		label: '110',
		value: 110
	},
	{
		label: '132',
		value: 132
	},
	{
		label: '150',
		value: 150
	},
	{
		label: '220',
		value: 220
	},
	{
		label: '275',
		value: 275
	},
	{
		label: '380',
		value: 380
	}
];

export const statusLookup = [
	{
		code: 0,
		min: undefined,
		max: undefined,
		title: 'Det oppstod en feil',
		message: 'Klarte ikke hente status på tilstand.'
	},
	{
		code: 1,
		min: 1.35,
		title: 'God tilstand',
		message: 'Ingen indikasjon på nedbrytning (som ny).'
	},
	{
		code: 2,
		min: 1.12,
		max: 1.349,
		title: 'Redusert tilstand',
		message: 'Noen indikasjoner på nedbrytning. Tilstanden er betydelig verre enn "som ny".'
	},
	{
		code: 3,
		min: 1,
		max: 1.119,
		title: 'Alvorlig tilstand',
		message: 'Alvorlig nedbrytning. Tilstanden er betydelig verre enn "som ny".'
	},
	{
		code: 4,
		min: 0,
		max: 0.999,
		title: 'Kritisk tilstand',
		message: 'Tilstanden er kritisk.'
	},
	{
		code: 5,
		title: 'Havari',
		max: 0,
		message: 'Feil / havari.'
	}
];

export const surveyedOptions: Record<string, string> = {
	NotSet: 'Ikke satt',
	NotControlled: 'Ikke kontrollert',
	Controlled: 'Kontrollert',
	DoesNotExist: 'Finnes ikke'
} as const;

export const qualityOptions = {
	// NotCalculated: 'Ikke kalkulert',
	// NotSet: 'Registrert', To match backend, the correct label is "Registrert". This does however not make much sense in our UI.
	NotSet: 'Ikke definert', 
	Ok: 'Ok',
	ReducedCondition: 'Redusert tilstand',
	SeriousCondition: 'Alvorlig tilstand'
} as const;

export const NrlIntegrationVoltageOptions = [
	{
		label: 'Distribusjonsnett (0 - 60 kV)',
		value: 'dnet',
		nrlFromVoltage: 0,
		nrlToVoltage: 59
	},
	{
		label: 'Regionalnett (60 kV og opp)',
		value: 'rnet',
		nrlFromVoltage: 60,
		nrlToVoltage: 1000
	},
	{
		label: 'Alt (0 - 1000 kV)',
		value: 'all',
		nrlFromVoltage: 0,
		nrlToVoltage: 1000
	}
] as const;


// Possible states of the side menu
export const SideMenuOptions = {
	DRAW_POLYGON: 'DRAW_POLYGON',
	ADD_POLE: 'ADD_POLE',
	ADD_LINE: 'ADD_LINE', // TODO: Deprecated, remove later
	ADD_LINES_NEW: 'ADD_LINES_NEW',
	SHOW_MAPLAYERS: 'SHOW_MAPLAYERS',
	SHOW_LAYERPICKINGINFO: 'SHOW_LAYERPICKINGINFO',
	SHOW_POINTCLOUDS: 'SHOW_POINTCLOUDS',
	SHOW_INFOGRID: 'SHOW_INFOGRID',
	TOGGLE_ENTITIES: 'TOGGLE_ENTITIES',
	POLYGON_SELECTED: 'POLYGON_SELECTED',
	POLYGON_SELECTION_RESULTS: 'POLYGON_SELECTION_RESULTS',
	MEASUREMENT_TOOLBAR: 'MEASUREMENT_TOOLBAR',
	LABEL_TOOL: 'LABEL_TOOL',
	RECTANGLE_TOOL: 'RECTANGLE_TOOL',
	DRAWING_TOOLS: 'DRAWING_TOOLS',
	AVIATION_OBSTACLE_SELECTED: 'AVIATION_OBSTACLE_SELECTED',
	CABLE_ENTITY_SELECTED: 'CABLE_ENTITY_SELECTED',
	TRENCH_ENTITY_SELECTED: 'TRENCH_ENTITY_SELECTED',
	IMAGE_AREA: 'IMAGE_AREA',
	NONE: 'NONE'
} as const;